import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Form, Spinner, Alert } from 'react-bootstrap';
import {
  getTenantAdmins,
  createTenantAdmin,
  resetUserPassword
} from '../../api/api';
import { showToast } from '../../utils/toast';

const TenantAdminsModal = ({ show, onHide, tenant }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: ''
  });

  const [resetPassword, setResetPassword] = useState({});
  const [saving, setSaving] = useState(false);

  /* -----------------------------
     Load Tenant Admins
  ------------------------------ */
  const loadAdmins = async () => {
    if (!tenant?.id) return;

    try {
      setLoading(true);
      const res = await getTenantAdmins(tenant.id);
      setAdmins(res.data);
    } catch (err) {
      setError('Failed to load tenant admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      loadAdmins();
    }
  }, [show]);

  /* -----------------------------
     Create Tenant Admin
  ------------------------------ */
  const handleCreateAdmin = async () => {
    if (!newAdmin.email || !newAdmin.password) {
      showToast('Email and password are required', 'warning');
      return;
    }

    try {
      setSaving(true);
      await createTenantAdmin(tenant.id, newAdmin);
      setNewAdmin({ email: '', password: '' });
      await loadAdmins();
    } catch (err) {
      showToast('Failed to create tenant admin', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* -----------------------------
     Reset Password
  ------------------------------ */
  const handleResetPassword = async (userId) => {
    const pwd = resetPassword[userId];
    if (!pwd) {
      showToast('Enter a new password', 'info');
      return;
    }

    try {
      setSaving(true);
      await resetUserPassword(userId, pwd);
      setResetPassword(prev => ({ ...prev, [userId]: '' }));
      showToast('Password reset successful', 'success');
    } catch (err) {
      showToast('Failed to reset password', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* -----------------------------
     Render
  ------------------------------ */
  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Tenant Admins — {tenant?.name} ({tenant?.slug})
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger" role="alert">{error}</Alert>}

        {/* -------- Existing Admins -------- */}
        <h6 className="mb-2">Existing Tenant Admins</h6>

        {loading ? (
          <Spinner animation="border" aria-hidden="true" />
        ) : (
          <Table bordered size="sm">
            <thead className="table-light">
              <tr>
                <th>Email</th>
                <th>Created At</th>
                <th scope="col" style={{ width: '260px' }}>Reset Password</th>
              </tr>
            </thead>
            <tbody>
              {admins.length === 0 && (
                <tr>
                  <td colSpan="3" className="text-center text-muted">
                    No tenant admins found
                  </td>
                </tr>
              )}

              {admins.map(admin => (
                <tr key={admin.id}>
                  <td>{admin.email}</td>
                  <td>{new Date(admin.created_at).toLocaleString()}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="password"
                        size="sm"
                        placeholder="New password"
                        value={resetPassword[admin.id] || ''}
                        onChange={(e) =>
                          setResetPassword(prev => ({
                            ...prev,
                            [admin.id]: e.target.value
                          }))
                        }
                      />
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={saving}
                        onClick={() => handleResetPassword(admin.id)}
                      >
                        Reset
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <hr />

        {/* -------- Create New Admin -------- */}
        <h6 className="mb-2">Create Tenant Admin</h6>

        <Form>
          <Form.Group className="mb-2">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="admin@tenant.com"
              value={newAdmin.email}
              onChange={(e) =>
                setNewAdmin({ ...newAdmin, email: e.target.value })
              }
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Initial Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Minimum 8 characters"
              value={newAdmin.password}
              onChange={(e) =>
                setNewAdmin({ ...newAdmin, password: e.target.value })
              }
            />
            <Form.Text className="text-muted">
              Role will be <strong>tenant_admin</strong> (cannot be changed).
            </Form.Text>
          </Form.Group>

          <Button
            variant="primary"
            disabled={saving}
            onClick={handleCreateAdmin}
          >
            {saving ? 'Creating…' : 'Create Tenant Admin'}
          </Button>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TenantAdminsModal;
